/**
 * @fileoverview Welcome Celebration Animation
 * 
 * Full-screen animated overlay shown after all onboarding steps complete.
 * Features glowing light orbs, the WNTD wordmark with breathing pulse,
 * and a smooth blur-to-sharp headline reveal.
 * 
 * Tap anywhere to skip.
 */

import { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import wordmark from '@/assets/wntd-wordmark.svg';

interface WelcomeCelebrationProps {
  onComplete: () => void;
}

const TOTAL_DURATION = 5500;

interface Orb {
  id: number;
  angle: number;
  distance: number;
  size: number;
  opacity: number;
  delay: number;
  duration: number;
}

function generateOrbs(): Orb[] {
  // Inner ring – smaller, faster, brighter
  const inner = Array.from({ length: 24 }, (_, i) => ({
    id: i,
    angle: (i / 24) * Math.PI * 2 + (Math.random() - 0.5) * 0.5,
    distance: 80 + Math.random() * 140,
    size: 4 + Math.random() * 6,
    opacity: 0.6 + Math.random() * 0.4,
    delay: 1 + Math.random() * 0.6,
    duration: 1.4 + Math.random() * 0.8,
  }));
  // Outer ring – larger, slower, more transparent
  const outer = Array.from({ length: 16 }, (_, i) => ({
    id: 24 + i,
    angle: (i / 16) * Math.PI * 2 + (Math.random() - 0.5) * 0.6,
    distance: 200 + Math.random() * 160,
    size: 8 + Math.random() * 12,
    opacity: 0.2 + Math.random() * 0.25,
    delay: 1.2 + Math.random() * 0.8,
    duration: 2 + Math.random() * 1,
  }));
  return [...inner, ...outer];
}

export function WelcomeCelebration({ onComplete }: WelcomeCelebrationProps) {
  const [phase, setPhase] = useState(0);
  const [visible, setVisible] = useState(true);
  const [orbs] = useState(generateOrbs);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 1000),
      setTimeout(() => setPhase(2), 2500),
      setTimeout(() => setPhase(3), 4000),
      setTimeout(() => {
        setVisible(false);
        setTimeout(onComplete, 600);
      }, TOTAL_DURATION),
    ];
    return () => timers.forEach(clearTimeout);
  }, [onComplete]);

  const handleSkip = useCallback(() => {
    setVisible(false);
    setTimeout(onComplete, 400);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-black overflow-hidden cursor-pointer"
          onClick={handleSkip}
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, y: -40 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          {/* Radial glow */}
          {phase >= 1 && (
            <motion.div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0, scale: 0.3 }}
              animate={{ opacity: 0.4, scale: 1.6 }}
              transition={{ duration: 2, ease: 'easeOut' }}
            >
              <div
                className="w-[500px] h-[500px] rounded-full"
                style={{
                  background: 'radial-gradient(circle, hsla(15, 100%, 62%, 0.5) 0%, hsla(15, 100%, 62%, 0.12) 40%, transparent 70%)',
                }}
              />
            </motion.div>
          )}

          {/* Light orbs */}
          {phase >= 1 && orbs.map((orb) => {
            const x = Math.cos(orb.angle) * orb.distance;
            const y = Math.sin(orb.angle) * orb.distance;
            return (
              <motion.div
                key={orb.id}
                className="absolute rounded-full pointer-events-none"
                style={{
                  left: '50%',
                  top: '50%',
                  width: orb.size,
                  height: orb.size,
                  background: `hsla(15, 100%, 62%, ${orb.opacity})`,
                  boxShadow: `0 0 ${orb.size * 2}px ${orb.size}px hsla(15, 100%, 62%, ${orb.opacity * 0.5})`,
                }}
                initial={{ x: 0, y: 0, opacity: 0, scale: 0 }}
                animate={{
                  x,
                  y,
                  opacity: [0, orb.opacity, orb.opacity * 0.6, 0],
                  scale: [0, 1, 0.8, 0.3],
                }}
                transition={{
                  delay: orb.delay,
                  duration: orb.duration,
                  ease: 'easeOut',
                }}
              />
            );
          })}

          {/* Logo – fixed position so it never shifts */}
          <motion.div
            className="absolute z-10"
            style={{ top: '38%', left: '50%', x: '-50%', y: '-50%' }}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ opacity: { duration: 0.6, ease: 'easeOut' }, scale: { duration: 0.8, ease: 'easeOut' } }}
          >
            <motion.div
              animate={phase >= 1
                ? {
                    scale: [1, 1.03, 1],
                    filter: ['drop-shadow(0 0 20px hsla(15, 100%, 62%, 0.6))', 'drop-shadow(0 0 40px hsla(15, 100%, 62%, 0.8))', 'drop-shadow(0 0 20px hsla(15, 100%, 62%, 0.6))'],
                  }
                : {}
              }
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img src={wordmark} alt="WNTD" className="w-48 h-auto" />
            </motion.div>
          </motion.div>

          {/* Headline – positioned below logo, also absolute */}
          {phase >= 2 && (
            <motion.div
              className="absolute z-10 text-center px-8"
              style={{ top: '55%', left: '50%', x: '-50%' }}
              initial={{ opacity: 0, y: 24, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <h1 className="text-2xl font-bold text-white tracking-tight leading-tight">
                See yourself, differently
              </h1>
              <motion.p
                className="text-sm text-white/60 mt-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.8 }}
              >
                Your style journey starts now
              </motion.p>
            </motion.div>
          )}

          {/* Skip hint */}
          <motion.p
            className="absolute bottom-12 text-xs text-white/30"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 0.5 }}
          >
            Tap to skip
          </motion.p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
